import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query';
import { PROJECTS_API_ENDPOINTS as cfg } from './config';
import type { Project, TelegramGroupProjectAccount, TelegramGroupProjectMapping } from './types';

type Definitions = DefinitionsFromApi<typeof generatedApi>;

/**
 * `undefined` in place of the documented array signals a `200` that didn't
 * match the SKY-4486 contract — an unexpected backend regression rather than
 * an anticipated state, now that the live endpoint has deployed the shape
 * below. This is deliberately distinct from a validly empty `[]` (no
 * Telegram group is mapped to a project yet). The feature layer treats
 * either this or the query's own `error` (502/504 — MM Client unreachable/
 * timed out) the same way: render the group list ungrouped rather than
 * blocking or emptying it.
 */
type UpdatedDefinitions = Omit<Definitions, typeof cfg.listProjects.generated> & {
  [cfg.listProjects.generated]: OverrideResultType<
    Definitions[typeof cfg.listProjects.generated],
    TelegramGroupProjectMapping[] | undefined
  >;
};

const isProject = (value: unknown): value is Project =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  typeof value.id === 'number' &&
  'name' in value &&
  typeof value.name === 'string';

const isTelegramGroupProjectAccount = (value: unknown): value is TelegramGroupProjectAccount =>
  typeof value === 'object' &&
  value !== null &&
  'accountId' in value &&
  typeof value.accountId === 'number' &&
  'isActive' in value &&
  typeof value.isActive === 'boolean' &&
  'projectId' in value &&
  (value.projectId === null || typeof value.projectId === 'number');

/**
 * `accounts` is validated even though nothing reads it yet: the returned
 * value is typed as the full documented contract, so a consumer added later
 * would trust a field this guard never checked.
 */
const isTelegramGroupProjectMapping = (value: unknown): value is TelegramGroupProjectMapping =>
  typeof value === 'object' &&
  value !== null &&
  'chatId' in value &&
  typeof value.chatId === 'string' &&
  'projects' in value &&
  Array.isArray(value.projects) &&
  value.projects.every(isProject) &&
  // Documented as "distinct projects behind those accounts" — a duplicate id
  // would silently overwrite an earlier project's name when
  // `groupTradingGroupsByProject` keys its sections by `project.id`.
  new Set(value.projects.map((project) => project.id)).size === value.projects.length &&
  'accounts' in value &&
  Array.isArray(value.accounts) &&
  value.accounts.every(isTelegramGroupProjectAccount);

export const projectsApi = generatedApi.enhanceEndpoints<never, UpdatedDefinitions>({
  endpoints: {
    [cfg.listProjects.generated]: {
      transformResponse: (response: unknown): TelegramGroupProjectMapping[] | undefined => {
        const data =
          typeof response === 'object' && response !== null && 'data' in response
            ? response.data
            : undefined;

        if (!Array.isArray(data) || !data.every(isTelegramGroupProjectMapping)) {
          // The live endpoint now serves the documented contract, so a
          // mismatch here is an unexpected backend regression worth paging
          // on — `console.error`, matching `telegram-groups-api.ts`'s
          // `trackMissingJobId` pattern for an off-contract response. The raw
          // `response` isn't logged: it's unbounded, unshaped data that would
          // otherwise spam the console/Sentry on every occurrence.
          console.error('listProjects: response did not match the documented SKY-4486 contract');
          return undefined;
        }

        return data;
      },
    },
  },
});

export const projectsApiEndpoints = createEndpointExports(cfg, projectsApi);

export const hookExports: HookExports<typeof cfg, typeof projectsApi> = createHookExports(
  cfg,
  projectsApi,
);
